/**
 * ReplaceProductDialog
 *
 * Used in Comparison Mode to swap a cart line item with a different brand
 * that has the SAME thickness and SAME size — no manual attribute picking.
 *
 * Shows a list of product cards from /api/products?match_thickness=&match_size=,
 * one click replaces the item in place. Falls back to "Browse all brands"
 * (the long ProductsPage flow) when no exact matches exist.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Search, ArrowRight, GitCompare } from 'lucide-react';
import { api, SQFT_CATEGORIES, sizeToSqft } from '../../lib/store';

function pickRateForProduct(product, size) {
  // Returns the per-(sqft|sheet|piece) rate matching `size` if possible, else
  // the product's base_rate, else null.
  const cr = product?.codes_and_rates || [];
  if (size && Array.isArray(cr) && cr.length > 0) {
    const match = cr.find(r => (r?.sizes || []).some(s => String(s).toLowerCase() === String(size).toLowerCase()));
    if (match && match.rate != null) return Number(match.rate);
    // Fallback to first code with a rate
    const any = cr.find(r => r?.rate != null);
    if (any) return Number(any.rate);
  }
  if (product?.base_rate != null) return Number(product.base_rate);
  return null;
}

function pickRateTypeForProduct(product) {
  const cr = product?.codes_and_rates || [];
  if (Array.isArray(cr) && cr.length > 0 && cr[0]?.rate_type) return cr[0].rate_type;
  return product?.rate_type || 'per sheet';
}

export function ReplaceProductDialog({ open, onOpenChange, originalItem, onReplace, onBrowseAll }) {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');

  const category = originalItem?.main_category || '';
  const size = originalItem?.size || '';
  const thickness = originalItem?.thickness || '';

  useEffect(() => {
    if (!open || !category) return;
    setLoading(true);
    setError('');
    api.getProductsMatching({ category, match_thickness: thickness, match_size: size, limit: 100 })
      .then(res => {
        const list = (res?.data?.products || []).filter(p => p.id !== originalItem?.product_id);
        setProducts(list);
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Failed to load matching products');
        setProducts([]);
      })
      .finally(() => setLoading(false));
  }, [open, category, size, thickness, originalItem?.product_id]);

  const handlePick = (product) => {
    const isSqftCategory = SQFT_CATEGORIES.has(category);
    const baseRate = pickRateForProduct(product, size);
    if (baseRate == null) {
      // Eg: product has no rate set; let the user fall back to manual flow.
      onBrowseAll?.();
      onOpenChange(false);
      return;
    }
    const sqftPerSheet = isSqftCategory && size ? sizeToSqft(size, category) : 0;
    const calculatedRate = (isSqftCategory && sqftPerSheet > 0) ? baseRate * sqftPerSheet : baseRate;
    const rateType = pickRateTypeForProduct(product);
    const unit = isSqftCategory ? 'NOS' : (rateType ? rateType.replace('per ', '').toUpperCase() : 'NOS');

    onReplace?.(product, {
      size: size || (product.available_sizes?.[0] || ''),
      thickness: thickness || product.thickness || '',
      quantity: originalItem?.quantity || 1,
      unit,
      baseRate,
      calculatedRate,
    });
    onOpenChange(false);
  };

  const heading = useMemo(() => {
    const parts = [];
    if (thickness) parts.push(thickness);
    if (size) parts.push(size);
    return parts.length ? parts.join(' · ') : 'Same specs';
  }, [size, thickness]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col" data-testid="replace-product-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <GitCompare className="w-4 h-4 text-[#DAA520]" />
            Replace with another brand
          </DialogTitle>
          <p className="text-xs text-[#8F9485] mt-1">
            Showing {category} products matching <span className="font-semibold">{heading}</span>. Click a card to swap in place.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2 px-1" data-testid="replace-product-list">
          {loading && (
            <div className="flex items-center justify-center py-12 text-[#8F9485]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Searching…
            </div>
          )}
          {!loading && error && (
            <p className="text-sm text-[#B93838] py-4 text-center" data-testid="replace-error">{error}</p>
          )}
          {!loading && !error && products.length === 0 && (
            <div className="text-center py-10" data-testid="replace-no-matches">
              <Search className="w-8 h-8 text-[#8F9485] mx-auto mb-3" />
              <p className="text-sm text-[#5C6150] mb-2">No exact match for {heading || 'this item'}.</p>
              <p className="text-xs text-[#8F9485]">Try the full product picker for more options.</p>
            </div>
          )}
          {!loading && !error && products.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {products.map(p => {
                const rate = pickRateForProduct(p, size);
                const rateType = pickRateTypeForProduct(p);
                return (
                  <button
                    key={p.id}
                    onClick={() => handlePick(p)}
                    className="text-left p-3 border border-[#E2E6D9] rounded-lg hover:border-[#DAA520] hover:bg-[#FDF6E1]/30 transition-colors group"
                    data-testid={`replace-product-card-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1A1C16] truncate">{p.brand_name}</p>
                        <p className="text-xs text-[#5C6150] truncate">{p.subcategory || p.product_name || '—'}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#8F9485] group-hover:text-[#DAA520] flex-shrink-0 mt-0.5" />
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {p.thickness && <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-5">{p.thickness}</Badge>}
                      {(p.available_sizes || []).slice(0, 3).map(s => (
                        <Badge key={s} variant="outline" className="text-[10px] py-0 px-1.5 h-5">{s}</Badge>
                      ))}
                    </div>
                    {rate != null && (
                      <p className="text-xs font-mono text-[#556B2F] mt-2">
                        ₹{Number(rate).toLocaleString('en-IN')} <span className="text-[#8F9485]">{rateType ? `/ ${rateType.replace('per ', '')}` : ''}</span>
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2 border-t border-[#E2E6D9] sticky bottom-0 bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="sm:flex-1 h-9 text-sm" data-testid="replace-cancel-btn">
            Cancel
          </Button>
          <Button onClick={onBrowseAll} className="sm:flex-1 h-9 text-sm bg-[#556B2F] hover:bg-[#465926] text-white" data-testid="replace-browse-all-btn">
            <Search className="w-3.5 h-3.5 mr-1" /> Browse all brands
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ReplaceProductDialog;
